// Client for the word practice API (api/src/app/practice.go). The practice
// screen keeps a small queue of cards and talks to the server imperatively,
// so most calls here are plain async functions rather than query hooks.
import { QueryClient, useQuery } from "@tanstack/react-query";
import { apiUrl, fetchParamsWithAuth } from "./queries";

export type Direction = "recognize" | "produce";

export type CardFormat =
  | "recognize_word"
  | "recognize_sentence"
  | "produce_word"
  | "produce_sentence";

// Grade of an answer: 1 forgot, 2 hard, 3 medium, 4 easy.
export type Grade = 1 | 2 | 3 | 4;

export interface PracticeCard {
  entryId: number;
  direction: Direction;
  format: CardFormat;
  isNew: boolean;
  leech: boolean;
  // The word in the learned language, e.g. "der Baum".
  word: string;
  partOfSpeech: string;
  gender: string;
  // The meaning in the spoken language.
  meaning: string;
  forms: string[];
  definition: string;
  mnemonic: string;
  // Sentence fields are empty when the card has no sentence.
  sentenceId: number;
  sentence: string;
  target: string;
  translation: string;
  translationTarget: string;
  formNote: string;
  answer: string;
  hints: string[];
}

export type PausedReason =
  | ""
  | "reviews_due"
  | "consolidating"
  | "struggling"
  | "goal_reached"
  | "daily_cap";

export interface PracticeStatus {
  due: number;
  newToday: number;
  dailyGoal: number;
  paused: PausedReason;
  canLearnMore: boolean;
  learnMoreSize: number;
  reviewsToday: number;
  // CEFR band new words come from, e.g. "B1".
  wordLevel: string;
  // "default" until the user took or skipped the placement game.
  levelSource: "default" | "skipped" | "placement";
  nextDueAt: Date | null;
}

export interface GenderNoun {
  entryId: number;
  word: string;
  gender: "masculine" | "feminine" | "neuter";
  meaning: string;
}

export interface PlacementWord {
  id: number;
  word: string;
}

// The user's UTC offset in minutes, which places their practice days.
function tzOffset(): string {
  return (-new Date().getTimezoneOffset()).toString();
}

async function call(url: URL, method: string, body?: unknown): Promise<any> {
  const params: RequestInit = await fetchParamsWithAuth(method);
  if (body !== undefined) {
    params.body = JSON.stringify(body);
    (params.headers as Headers).set("Content-Type", "application/json");
  }
  const res = await fetch(url, params);
  if (!res.ok) {
    console.error("Unexpected result for", url.pathname, res.status);
    throw new Error(`Unexpected result for ${url.pathname}`);
  }
  return res.json();
}

function toCard(card: any): PracticeCard {
  return {
    entryId: card.entry_id,
    direction: card.direction,
    format: card.format,
    isNew: card.is_new,
    leech: card.leech,
    word: card.word,
    partOfSpeech: card.part_of_speech,
    gender: card.gender,
    meaning: card.meaning,
    forms: card.forms ?? [],
    definition: card.definition ?? "",
    mnemonic: card.mnemonic ?? "",
    sentenceId: card.sentence_id ?? 0,
    sentence: card.sentence ?? "",
    target: card.target ?? "",
    translation: card.translation ?? "",
    translationTarget: card.translation_target ?? "",
    formNote: card.form_note ?? "",
    answer: card.answer,
    hints: card.hints ?? [],
  };
}

function toStatus(status: any): PracticeStatus {
  return {
    due: status.due,
    newToday: status.new_today,
    dailyGoal: status.daily_goal,
    paused: status.paused,
    canLearnMore: status.can_learn_more,
    learnMoreSize: status.learn_more_size,
    reviewsToday: status.reviews_today,
    wordLevel: status.word_level,
    levelSource: status.level_source,
    nextDueAt: status.next_due_at ? new Date(status.next_due_at) : null,
  };
}

// fetchNextCards asks for up to count more cards, skipping the words of the
// cards still in the queue. A storyId other than "" draws only from the
// words of that story (a story session, see the practice package).
export async function fetchNextCards(
  l: string,
  r: string,
  queue: PracticeCard[],
  count: number,
  storyId: string,
): Promise<{ cards: PracticeCard[]; status: PracticeStatus }> {
  const url = apiUrl("/practice/next");
  url.searchParams.append("l", l);
  url.searchParams.append("r", r);
  url.searchParams.append("tz_offset", tzOffset());
  url.searchParams.append("count", count.toString());
  if (queue.length > 0) {
    url.searchParams.append("queue", queue.map((card) => card.entryId).join(","));
  }
  if (storyId) {
    url.searchParams.append("story_id", storyId);
  }
  const result = await call(url, "GET");
  return { cards: (result.cards ?? []).map(toCard), status: toStatus(result.status) };
}

// submitAnswer sends one grade. reviewId makes a retried request count once.
export async function submitAnswer(
  l: string,
  r: string,
  card: PracticeCard,
  grade: Grade,
  hintsUsed: number,
  reviewId: string,
): Promise<void> {
  const url = apiUrl("/practice/answer");
  url.searchParams.append("l", l);
  url.searchParams.append("r", r);
  url.searchParams.append("tz_offset", tzOffset());
  await call(url, "POST", {
    review_id: reviewId,
    entry_id: card.entryId,
    direction: card.direction,
    format: card.format,
    grade,
    hints_used: hintsUsed,
  });
}

export async function learnMore(r: string): Promise<PracticeStatus> {
  const url = apiUrl("/practice/learn-more");
  url.searchParams.append("r", r);
  url.searchParams.append("tz_offset", tzOffset());
  return toStatus(await call(url, "POST"));
}

export async function fetchPlacementWords(r: string): Promise<PlacementWord[]> {
  const url = apiUrl("/practice/placement");
  url.searchParams.append("r", r);
  const result = await call(url, "GET");
  return result.words ?? [];
}

// submitPlacement sends the placement answers, or skip, and returns the CEFR
// band new words will come from.
export async function submitPlacement(
  l: string,
  r: string,
  answers: { id: number; known: boolean }[] | "skip",
): Promise<string> {
  const url = apiUrl("/practice/placement");
  url.searchParams.append("l", l);
  url.searchParams.append("r", r);
  url.searchParams.append("tz_offset", tzOffset());
  const result = await call(url, "POST", answers === "skip" ? { skip: true } : { answers });
  return result.word_level ?? "";
}

export async function fetchGenderRound(l: string, r: string): Promise<GenderNoun[]> {
  const url = apiUrl("/practice/gender-round");
  url.searchParams.append("l", l);
  url.searchParams.append("r", r);
  const result = await call(url, "GET");
  return (result.nouns ?? []).map((noun: any) => ({
    entryId: noun.entry_id,
    word: noun.word,
    gender: noun.gender,
    meaning: noun.meaning,
  }));
}

export async function fetchPracticeStatus(r: string): Promise<PracticeStatus> {
  const url = apiUrl("/practice/status");
  url.searchParams.append("r", r);
  url.searchParams.append("tz_offset", tzOffset());
  return toStatus(await call(url, "GET"));
}

// usePracticeStatusQuery backs the "N due" hint outside the practice screen.
export function usePracticeStatusQuery(r: string, enabled: boolean) {
  return useQuery<PracticeStatus>({
    queryKey: ["practice-status", r],
    queryFn: () => fetchPracticeStatus(r),
    enabled,
    staleTime: 60_000,
    retry: 1,
  });
}

async function fetchStoryPracticeCount(
  r: string,
  storyId: string,
): Promise<number> {
  const url = apiUrl("/practice/story-count");
  url.searchParams.append("r", r);
  url.searchParams.append("story_id", storyId);
  url.searchParams.append("tz_offset", tzOffset());
  const result = await call(url, "GET");
  return result.count ?? 0;
}

// useStoryPracticeCountQuery counts the words of a story that a story
// session would practice now. Reading the story changes the count, so it is
// fetched again whenever it's shown and after the user looks up or saves a
// word in the story (invalidateStoryPracticeCount).
export function useStoryPracticeCountQuery(
  r: string,
  storyId: string,
  enabled: boolean,
) {
  return useQuery<number>({
    queryKey: ["practice-story-count", r, storyId],
    queryFn: () => fetchStoryPracticeCount(r, storyId),
    enabled,
    staleTime: 0,
    retry: 1,
  });
}

export function invalidateStoryPracticeCount(
  queryClient: QueryClient,
  r: string,
  storyId: string,
) {
  queryClient.invalidateQueries({
    queryKey: ["practice-story-count", r, storyId],
  });
}

// Audio is fetched with the Authorization header (which a media element
// can't send) and kept as object URLs until the signed-in user changes:
// words and sentences come back, and the bytes are small.
const audioUrls = new Map<string, Promise<string>>();

export function forgetPracticeAudio() {
  audioUrls.forEach((objectUrl) =>
    objectUrl.then(
      (url) => URL.revokeObjectURL(url),
      () => {},
    ),
  );
  audioUrls.clear();
}

export function practiceAudioUrl(source: { entryId: number } | { sentenceId: number }): Promise<string> {
  const url = apiUrl("/practice/audio");
  if ("sentenceId" in source) {
    url.searchParams.append("sentence_id", source.sentenceId.toString());
  } else {
    url.searchParams.append("entry_id", source.entryId.toString());
  }
  const key = url.toString();
  let objectUrl = audioUrls.get(key);
  if (!objectUrl) {
    objectUrl = (async () => {
      const res = await fetch(url, await fetchParamsWithAuth("GET"));
      if (!res.ok) {
        throw new Error("Unexpected result for practice audio");
      }
      return URL.createObjectURL(await res.blob());
    })();
    // A failed fetch is forgotten, so the next play tries again - unless the
    // map was cleared meanwhile and may hold a newer fetch.
    objectUrl.catch(() => {
      if (audioUrls.get(key) === objectUrl) audioUrls.delete(key);
    });
    audioUrls.set(key, objectUrl);
  }
  return objectUrl;
}
